import {
  ChangeEventHandler,
  forwardRef,
  KeyboardEventHandler,
  memo,
  MouseEvent,
  MouseEventHandler,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import cn from 'classnames';

import { Checkbox } from '../Checkbox';

import { FieldSelectView } from './FieldSelectView';
import { useClickOutSide, useListScrollByKeyBoard } from './selectHooks';
import { BaseSelectProps, Option } from './types';

import { EmptyCase, List, MainWrapper, OptionItemMultiple } from './styles';

type SelectMultipleProps = BaseSelectProps & {
  // main props
  value?: Array<Option['value']> | null;
  onChange?: (value: Array<Option['value']> | null) => void;
  // select manage props
  keyBoardHandlers: (
    commitSelection: (idx: number | null) => void,
    listLength?: number,
  ) => KeyboardEventHandler;
  onToggleList: () => void;
  onClickReset: MouseEventHandler;
  onKeyDownReset: KeyboardEventHandler;
  open: boolean;
  activeIndex: number | null;
};

const SelectMultipleInner = forwardRef<HTMLSelectElement, SelectMultipleProps>(
  (
    {
      // main props
      label,
      name,
      options,
      value,
      status,
      onChange,
      disabled = false,
      required,
      errorText,
      iconAfter,
      withSearch,
      className,
      // select manage props
      keyBoardHandlers,
      onToggleList,
      onClickReset,
      onKeyDownReset,
      open,
      activeIndex,
    },
    ref,
  ) => {
    const listboxId = useId();
    const labelId = useId();
    const optionIdBase = useId();
    const errorTextId = useId();
    const fieldViewRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLUListElement>(null);
    const searchViewRef = useRef<HTMLInputElement>(null);
    const optionRefs = useRef<(HTMLLIElement | null)[]>([]);
    const hasError = status === 'error';
    const activeId = open ? `${optionIdBase}-${activeIndex}` : undefined;

    const [inputValue, setInputValue] = useState('');
    const normalizedFilter =
      withSearch && open && inputValue.trim().length > 0 ? inputValue.trim().toLowerCase() : '';
    const optionsToRender = useMemo(() => {
      if (!withSearch || !open || !normalizedFilter) {
        return options;
      }
      return options.filter((opt) => opt.content.label.toLowerCase().includes(normalizedFilter));
    }, [normalizedFilter, options, open, withSearch]);
    const safeValue = value ?? [];
    const valueSet = useMemo(() => new Set(safeValue), [safeValue]);

    const selectedOptions = useMemo(
      () => options.filter((opt) => valueSet.has(opt.value)),
      [options, valueSet],
    );

    const selectedLabels = selectedOptions.map((opt) => (
      <span className="chip" key={opt.value}>
        {opt.content.label}
      </span>
    ));

    const selectedValues = selectedOptions.map((opt) => String(opt.value));

    useEffect(() => {
      if (!open && inputValue) {
        setInputValue('');
      }
    }, [open, inputValue]);

    useEffect(() => {
      if (open) {
        searchViewRef.current?.focus();
      } else {
        searchViewRef.current?.blur();
      }
    }, [safeValue, open]);

    // Close by clicking outside
    useClickOutSide({ open, onToggleList, fieldViewRef, listRef });

    // Scroll by using (ArrowUp | ArrowDown | Home | End) keys
    useListScrollByKeyBoard({ activeIndex, open, optionRefs });

    const commitSelection = (idx: number | null) => {
      if (idx === null) return;
      const opt = optionsToRender[idx];
      if (!opt) return;

      const exist = valueSet.has(opt.value);
      const next = exist ? safeValue.filter((v) => v !== opt.value) : [...safeValue, opt.value];
      onChange?.(next);
    };
    const handleKeyDown = keyBoardHandlers(commitSelection, optionsToRender.length);

    const handleSearch: ChangeEventHandler<HTMLInputElement> = ({ target }) => {
      setInputValue(target.value);
    };

    const handleResetClick: MouseEventHandler = (e) => {
      onClickReset(e);
      setInputValue('');
    };

    const handleResetKeyDown: KeyboardEventHandler = (e) => {
      onKeyDownReset(e);
      if ([' ', 'Enter'].includes(e.key)) {
        setInputValue('');
      }
    };

    const handleFieldClick = () => {
      if (!open && withSearch) {
        searchViewRef.current?.focus();
      }
      onToggleList();
    };

    return (
      <MainWrapper className={cn(className, { required })}>
        <div className="select-wrapper">
          <FieldSelectView
            ref={fieldViewRef}
            labelId={labelId}
            listboxId={listboxId}
            activeId={activeId}
            hasError={hasError}
            errorTextId={errorTextId}
            onKeyDown={handleKeyDown}
            onFieldClick={handleFieldClick}
            onKeyDownReset={handleResetKeyDown}
            onClickReset={handleResetClick}
            onSearch={handleSearch}
            label={label}
            selectedView={selectedLabels}
            iconAfter={iconAfter}
            status={status}
            disabled={disabled}
            required={required}
            open={open}
            withSearch={withSearch}
            searchViewRef={searchViewRef}
            inputValue={inputValue}
          />

          {/* Popup-list */}
          {open && (
            <List
              className="uikitScrollbar"
              ref={listRef}
              role="listbox"
              id={listboxId}
              aria-labelledby={labelId}
              aria-multiselectable="true"
            >
              {optionsToRender.length > 0 ? (
                optionsToRender.map((opt, idx) => {
                  const id = `${optionIdBase}-${idx}`;
                  const selected = valueSet.has(opt.value);
                  const active = idx === activeIndex;
                  const handlerClick = (e: MouseEvent<HTMLElement>) => {
                    e.preventDefault();
                    commitSelection(idx);
                  };

                  return (
                    <OptionItemMultiple
                      ref={(el) => {
                        optionRefs.current[idx] = el;
                      }}
                      id={id}
                      key={opt.value}
                      role="option"
                      aria-selected={selected}
                      onClick={handlerClick}
                    >
                      <Checkbox
                        onClick={handlerClick}
                        type="checkbox"
                        tabIndex={-1}
                        checked={selected}
                        readOnly
                        className={cn('checkbox', { active })}
                      />
                      <div className="option-content">
                        <div className="option-label">{opt.content.label}</div>
                        {Boolean(opt.content.description) && (
                          <div className="option-description">{opt.content.description}</div>
                        )}
                      </div>
                    </OptionItemMultiple>
                  );
                })
              ) : (
                <EmptyCase>No matches found for your search</EmptyCase>
              )}
            </List>
          )}

          {/* Native select hidden */}
          <select
            ref={ref}
            name={name}
            multiple
            value={selectedValues}
            onChange={() => {}}
            aria-hidden="true"
            tabIndex={-1}
            className="oouk-select-visually-hidden"
            required={required}
            disabled={disabled}
          >
            {options.map((opt, idx) => (
              <option key={idx} value={idx}>
                {opt.content.label}
              </option>
            ))}
          </select>
        </div>

        {hasError && (
          <div className="error-text" id={errorTextId} role="alert">
            {errorText}
          </div>
        )}
      </MainWrapper>
    );
  },
);

SelectMultipleInner.displayName = 'SelectMultiple';
export const SelectMultiple = memo(SelectMultipleInner) as typeof SelectMultipleInner;
